/**
 * NEON RAPIDS — cartridge palette.
 *
 * A neon river-canyon at night, seen top-down as the ship flies up-river.
 * The signature read is FUEL: a gauge that bleeds constantly and glows
 * green→amber→red as the tank empties. The river is a dark electric-teal
 * channel between lethal canyon walls; the player ship is hot-cyan so it
 * pops on the water, enemies are magenta/red threats, and fuel depots are
 * a friendly green you refill over (but must NOT shoot).
 */

// ---- Canyon walls / off-river ----
export const WALL_BASE = '#1a0e2e';      // canyon rock — lethal to touch
export const WALL_EDGE = '#ff3d9a';      // magenta rim glow on the bank
export const WALL_TEXTURE = '#2a1642';   // striated rock band

// ---- River water ----
export const WATER_TOP = '#062a34';      // deep teal at the far edge
export const WATER_BOTTOM = '#0a3a48';   // lighter teal near the ship
export const WATER_SHIMMER = '#1fd6e6';  // cyan surface glint
export const WATER_WAKE = '#7af3ff';     // wake behind the ship

// ---- Player ship ----
export const SHIP_BODY = '#29e6ff';      // hot cyan hull
export const SHIP_DARK = '#0a6a8a';      // hull shadow
export const SHIP_GLOW = '#a0fff0';      // canopy / thruster glow
export const SHIP_THRUST = '#ffd23d';    // engine flame

// ---- Bullets ----
export const BULLET = '#ffea00';         // gold tracer
export const BULLET_GLOW = '#fff2a0';

// ---- Enemies ----
export const ENEMY_SHIP = '#ff3088';     // magenta patrol boat
export const ENEMY_SHIP_DARK = '#8a1850';
export const ENEMY_HELI = '#ff6020';     // orange helicopter
export const ENEMY_HELI_DARK = '#a03810';
export const ENEMY_JET = '#ff3030';      // red jet (fast)
export const ENEMY_JET_DARK = '#8a1010';

// ---- Fuel depot (refill — do NOT shoot) ----
export const FUEL_BODY = '#27e060';      // friendly green
export const FUEL_DARK = '#136a30';
export const FUEL_MARK = '#a0ffc0';      // the "F" / pump glyph

// ---- Bridge (score gate + checkpoint) ----
export const BRIDGE_BODY = '#c0a060';    // steel-gold span
export const BRIDGE_DARK = '#6a5028';
export const BRIDGE_LIGHT = '#ffd23d';   // rivet lights

// ---- Explosions ----
export const BOOM_CORE = '#ffffff';
export const BOOM_HOT = '#ffd23d';
export const BOOM_MID = '#ff6020';
export const BOOM_SMOKE = '#7a3050';

// ---- Fuel gauge (the signature mechanic) ----
export const FUEL_FULL = '#27e060';      // healthy tank
export const FUEL_WARN = '#ffd23d';      // getting low
export const FUEL_CRIT = '#ff3030';      // near empty — flashing
export const FUEL_FRAME = '#7af3c8';     // gauge outline / label

// ---- HUD ----
export const HUD_LABEL = '#7af3c8';      // cyan labels, cabinet default
export const HUD_SCORE = '#ffea00';      // gold score numerals
export const HUD_LIVES = '#29e6ff';      // cyan life ships
export const HUD_DIST = '#a0fff0';       // pale-cyan distance

// ---- Cards (crash / start / checkpoint) ----
export const CARD_BG = '#08121e';        // deep river-night backdrop
export const CARD_BORDER = '#ff3030';    // red border = crash
export const CARD_BORDER_OK = '#27e060'; // green border = checkpoint
export const CARD_TEXT = '#ffea00';      // gold heading
export const CARD_SUB = '#7af3ff';       // cyan subtitle
export const CRASH_FLASH = '#ff3030';    // full-screen hit flash

// ---- Attract chrome ----
export const ATTRACT_TITLE = '#29e6ff';  // ship-cyan wordmark
export const ATTRACT_PROMPT = '#7af3c8'; // cyan "PRESS START"

/**
 * Tokens the cabinet samples for marquee tinting while this cartridge is
 * loaded. First entry is the single "this is the cartridge's color" pick.
 */
export const PALETTE_HINT: readonly string[] = [
  SHIP_BODY,   // ship cyan — the cartridge's signature
  FUEL_FULL,   // fuel-gauge green
  ENEMY_SHIP,  // magenta threat
  CARD_BG,     // river-night backdrop
];
